import { onActivated, onBeforeUnmount, onDeactivated, shallowRef, watch, type Ref } from 'vue'
import { DISTRIBUTE_MEDIA_VISIBILITY_THRESHOLD } from '@/constants/distribute'

interface UseElementVisibilityOptions {
  initialVisible?: boolean
  isVisible?: (entry: IntersectionObserverEntry) => boolean
  rootMargin?: string
  threshold?: number | readonly number[]
}

type VisibilityCallback = (entry: IntersectionObserverEntry) => void

interface VisibilityBucket {
  callbacksByElement: Map<Element, Set<VisibilityCallback>>
  observer: IntersectionObserver
}

const visibilityBuckets = new Map<string, VisibilityBucket>()

function normalizeThreshold(threshold: number | readonly number[] | undefined): number[] {
  if (Array.isArray(threshold)) {
    return threshold.slice().sort((left, right) => left - right)
  }

  if (typeof threshold === 'number') {
    return [threshold]
  }

  return [DISTRIBUTE_MEDIA_VISIBILITY_THRESHOLD]
}

function buildBucketKey(options: UseElementVisibilityOptions): string {
  return [options.rootMargin ?? '0px', normalizeThreshold(options.threshold).join(',')].join('|')
}

function getVisibilityBucket(options: UseElementVisibilityOptions): VisibilityBucket {
  const key = buildBucketKey(options)
  const existingBucket = visibilityBuckets.get(key)

  if (existingBucket) {
    return existingBucket
  }

  const callbacksByElement = new Map<Element, Set<VisibilityCallback>>()
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        callbacksByElement.get(entry.target)?.forEach((callback) => callback(entry))
      })
    },
    {
      rootMargin: options.rootMargin,
      threshold: normalizeThreshold(options.threshold),
    },
  )
  const bucket = { callbacksByElement, observer }

  visibilityBuckets.set(key, bucket)

  return bucket
}

export function useElementVisibility(
  elementRef: Ref<Element | null>,
  options: UseElementVisibilityOptions = {},
) {
  const isVisible = shallowRef(options.initialVisible ?? false)
  let activeBucketKey: string | null = null
  let activeCallback: VisibilityCallback | null = null
  let activeElement: Element | null = null
  let isObserverActive = true

  function resetVisibleState() {
    isVisible.value = options.initialVisible ?? false
  }

  function stopObserving() {
    if (!activeElement || !activeBucketKey || !activeCallback) {
      activeElement = null
      activeBucketKey = null
      activeCallback = null
      return
    }

    const bucket = visibilityBuckets.get(activeBucketKey)
    const callbacks = bucket?.callbacksByElement.get(activeElement)

    callbacks?.delete(activeCallback)

    if (callbacks && callbacks.size === 0) {
      bucket?.callbacksByElement.delete(activeElement)
      bucket?.observer.unobserve(activeElement)
    }

    if (bucket && bucket.callbacksByElement.size === 0) {
      bucket.observer.disconnect()
      visibilityBuckets.delete(activeBucketKey)
    }

    activeElement = null
    activeBucketKey = null
    activeCallback = null
  }

  function observeElement(element: Element | null) {
    stopObserving()
    resetVisibleState()

    if (!isObserverActive || !element || typeof window === 'undefined') {
      return
    }

    if (typeof IntersectionObserver === 'undefined') {
      isVisible.value = true
      return
    }

    const bucketKey = buildBucketKey(options)
    const bucket = getVisibilityBucket(options)
    const callback: VisibilityCallback = (entry) => {
      isVisible.value = options.isVisible?.(entry) ?? entry.isIntersecting
    }
    const callbacks = bucket.callbacksByElement.get(element) ?? new Set<VisibilityCallback>()

    callbacks.add(callback)
    bucket.callbacksByElement.set(element, callbacks)
    bucket.observer.observe(element)

    activeElement = element
    activeBucketKey = bucketKey
    activeCallback = callback
  }

  watch(
    elementRef,
    (element) => {
      observeElement(element)
    },
    { immediate: true, flush: 'post' },
  )

  onActivated(() => {
    isObserverActive = true
    observeElement(elementRef.value)
  })

  onDeactivated(() => {
    isObserverActive = false
    stopObserving()
    resetVisibleState()
  })

  onBeforeUnmount(() => {
    isObserverActive = false
    stopObserving()
  })

  return {
    isVisible,
  }
}
